import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion, useScroll, useSpring } from "framer-motion";

import logo from "@/assets/logo-white.png";
import { cn } from "@/lib/utils";

const navItems = [
  { label: "Funktion", href: "/funktion" },
  { label: "Preise", href: "/#preise" },
  { label: "FAQ", href: "/#faq" },
  { label: "Betoninfos", href: "/betoninfos" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.2 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  return (
    <nav className="fixed inset-x-0 top-0 z-50 px-4 pt-4">
      <div
        className={cn(
          "mx-auto max-w-6xl overflow-hidden rounded-[26px] border border-white/10 transition-all duration-500",
          scrolled
            ? "bg-background/90 shadow-[0_18px_70px_rgba(0,0,0,0.32)] backdrop-blur-xl"
            : "bg-background/55 backdrop-blur-md",
        )}
      >
        <motion.div className="h-px origin-left bg-primary/70" style={{ scaleX }} />

        <div
          className={cn(
            "flex items-center justify-between px-5 transition-all duration-300 sm:px-6",
            scrolled ? "h-[76px]" : "h-[88px]",
          )}
        >
          <Link to="/" className="flex items-center gap-4">
            <img
              src={logo}
              alt="BETON2GO by Romano"
              width={266}
              height={64}
              loading="eager"
              decoding="async"
              className={cn("w-auto transition-all duration-300", scrolled ? "h-9" : "h-11")}
            />
            <div className="hidden border-l border-white/10 pl-4 lg:block">
              <p className="info-label">Betontankstelle</p>
              <p className="mt-1 text-sm text-muted-foreground">Nalbach · Montag bis Samstag</p>
            </div>
          </Link>

          <div className="hidden items-center gap-7 md:flex">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className="group relative font-display text-sm uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-foreground"
              >
                {item.label}
                <span className="absolute inset-x-0 -bottom-2 h-px origin-left scale-x-0 bg-primary/80 transition-transform duration-300 group-hover:scale-x-100" />
              </Link>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setOpen((currentOpen) => !currentOpen)}
            className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-foreground transition-colors hover:bg-white/[0.07] md:hidden"
            aria-label={open ? "Menü schließen" : "Menü öffnen"}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        <AnimatePresence>
          {open ? (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden border-t border-white/10 md:hidden"
            >
              <div className="space-y-2 px-5 py-5">
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={item.href}
                      className="flex items-center justify-between rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-4 font-display text-base uppercase tracking-[0.16em] text-foreground"
                    >
                      {item.label}
                      <span className="text-xs text-muted-foreground">{String(index + 1).padStart(2, "0")}</span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;
